import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasOnError, HasOnSuccess } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getAccountsDataView,
    getCurrentAccountState,
} from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import loadAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadAccount";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import saveCurrentAccount from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/SaveCurrentAccount";
import updateAccountSettings from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/UpdateAccountSettings";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Modal from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { UserShowProductBrandImagesInterface } from "@tSchaedlerYesco/Handlers/SaveCurrentAccountExtended";
import CustomApplicationState from "@tSchaedlerYesco/Store/CustomApplicationState";
import updateAccountShowProductBrandImagesHandler from "@tSchaedlerYesco/Store/Data/ProductBrandImage/Handler/AccountShowProductBrandImageHandler";
import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    updateAccountSettings,
    updateAccountShowProductBrandImagesHandler,
    saveCurrentAccount,
    loadAccount,
};

const mapStateToProps = (state: CustomApplicationState, appstate: ApplicationState) => {
    return {
        editingAccount: getCurrentAccountState(state).value,
        emailErrorMessage: state.pages.accountSettings.emailErrorMessage,
        useEmailAsUserName: getSettingsCollection(state).accountSettings.useEmailAsUserName,
        changePasswordUrl: getPageLinkByPageType(state, "ChangePasswordPage")?.url,
        showProductBrandImages: state.tData.showProductImagesFlag.showProductBrandImages,
        account: state.pages.accountSettings.editingAccount,
        toggleaccount: getCurrentAccountState(state).value,
        userName: state.context.session.userName,
        accountsDataView: getAccountsDataView(state),
    };
};
type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AccountSettingsUserInformationStyles {
    userInformationTitle: TypographyProps;
    userInformationGridItem?: GridItemProps;
    changePasswordLink?: LinkPresentationProps;
    Wrapper?: InjectableCss;
}

export const userInformationStyles: AccountSettingsUserInformationStyles = {
    userInformationGridItem: {
        width: 12,
        css: css``,
    },
    changePasswordLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    userInformationTitle: {
        variant: "h2",
    },
    Wrapper: {
        css: css`
            display: flex;
            gap: 15px;
        `,
    },
};

const styles = userInformationStyles;

const Togglefilter = ({
    editingAccount,
    updateAccountSettings,
    updateAccountShowProductBrandImagesHandler,
    showProductBrandImages,
    saveCurrentAccount,
    account,
    toggleaccount,
    userName,
    loadAccount,
    accountsDataView,
}: Props) => {
    if (!userName) {
        return null;
    }

    React.useEffect(() => {
        if (!accountsDataView.value) {
            loadAccount({
                accountId: "current",
            });
        }
    }, [accountsDataView]);
    const [defaultShowProductBrandImages, setDefaultShowProductBrandImages] = useState(
        editingAccount?.properties?.showProductBrandImages === "true",
    );

    const toasterContext = useContext(ToasterContext);
    useEffect(() => {
        setDefaultShowProductBrandImages(showProductBrandImages);
    }, [showProductBrandImages]);

    useEffect(() => {
        if (editingAccount?.properties?.showProductBrandImages) {
            // const newShowProductBrandImages = !!editingAccount?.properties?.showProductBrandImages;
            if (editingAccount.properties.showProductBrandImages === "true") {
                setDefaultShowProductBrandImages(true);
            } else {
                setDefaultShowProductBrandImages(false);
            }
        }
    }, [editingAccount]);

    const handleCheckboxChange = (_event: React.SyntheticEvent, isChecked: boolean) => {
        setDefaultShowProductBrandImages(isChecked);
        updateAccountShowProductBrandImagesHandler({ showProductBrandImages: isChecked });

        const updatedObj: HasOnSuccess | HasOnError<string> | UserShowProductBrandImagesInterface | undefined = {
            ...obj,
            showProductBrandImages: isChecked, // Update showProductBrandImages based on isChecked
        };
        saveCurrentAccount(updatedObj);
    };
    const obj: HasOnSuccess | HasOnError<string> | UserShowProductBrandImagesInterface | undefined = {
        onSuccess: () => {
            toasterContext.addToast({ body: translate("Settings updated"), messageType: "success" });
        },
        onError: message => {
            toasterContext.addToast({ body: message, messageType: "warning" });
        },
        showProductBrandImages,
    };

    return (
        <>
            <GridContainer>
                <GridItem {...styles.userInformationGridItem}>
                    <CheckboxGroup
                        css={css`
                            margin-left: 20px !important;

                            legend {
                                margin-bottom: 0px !important;
                            }
                        `}
                        label={translate("Show Product Images and Brand Logos ")}
                    >
                        <Checkbox
                            checked={defaultShowProductBrandImages} // Checked based on state
                            onChange={handleCheckboxChange} // Handle changes
                            data-test-selector="accountSettings_showProductBrandImages"
                        >
                            {translate("Disable the Checkbox if you don't want Images or Brand Logos")}
                        </Checkbox>
                    </CheckboxGroup>
                </GridItem>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(Togglefilter),
    definition: {
        group: "Product List",
        displayName: "Toggle Filter For Images",
    },
};

export default widgetModule;
